import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import "./SalesPageNew.css";

const S3_BASE = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/images/social';
const socialLindsey = `${S3_BASE}/lindsey.webp`;
const socialOlivia = `${S3_BASE}/olivia.webp`;
const socialKeto = `${S3_BASE}/keto.webp`;
const socialAbi = `${S3_BASE}/abi.webp`;
const socialTegan = `${S3_BASE}/tegan.webp`;
const socialWilliam = `${S3_BASE}/william.webp`;
const socialGemma = `${S3_BASE}/gemma.webp`;
const socialLisa = `${S3_BASE}/lisa.webp`;

const images = [
   { src: socialLindsey, alt: "Lindsey Willis" },
   { src: socialOlivia, alt: "Olivia Mae Bradley" },
   { src: socialKeto, alt: "Keto Made Simple" },
   { src: socialAbi, alt: "Abi Mills" },
   { src: socialTegan, alt: "Slimming Tegan" },
   { src: socialWilliam, alt: "William" },
   { src: socialGemma, alt: "Gemma" },
   { src: socialLisa, alt: "Lisa Kaye" },
];

function SocialGrid() {
   const gridRef = useRef(null);
   const [inView, setInView] = useState(false);

   useEffect(() => {
      const el = gridRef.current;
      if (!el) return;
      const io = new IntersectionObserver(
         entries => {
            entries.forEach(e => {
               if (e.isIntersecting) { setInView(true); io.unobserve(e.target); }
            });
         },
         { threshold: 0.2 },
      );
      io.observe(el);
      return () => io.disconnect();
   }, []);

   return (
      <div ref={gridRef} style={{
         marginTop: 48, display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16,
      }}>
         {images.map((img, i) => (
            <div key={img.alt} style={{
               aspectRatio: '1', overflow: 'hidden', borderRadius: 16, background: 'var(--sp-bg-soft)',
               opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)',
               transition: 'opacity 600ms ease-out, transform 600ms ease-out',
               transitionDelay: `${i * 100}ms`,
            }}>
               <img src={img.src} alt={img.alt} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
            </div>
         ))}
      </div>
   );
}

export default function CounterSection({ windowWidth }) {
   const { t } = useTranslation();
   const ref = useRef(null);
   const [count, setCount] = useState(0);

   useEffect(() => {
      const el = ref.current;
      if (!el) return;
      let rafId = 0;
      let isAnimating = false;
      const animate = () => {
         isAnimating = true;
         const target = 8000;
         const duration = 1200;
         const start = performance.now();
         setCount(0);
         const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setCount(Math.round(target * eased));
            if (t < 1) rafId = requestAnimationFrame(tick);
            else isAnimating = false;
         };
         rafId = requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver(
         entries => {
            entries.forEach(e => {
               if (e.isIntersecting) { if (!isAnimating) animate(); }
               else { cancelAnimationFrame(rafId); isAnimating = false; setCount(0); }
            });
         },
         { threshold: 0.4 },
      );
      io.observe(el);
      return () => { cancelAnimationFrame(rafId); io.disconnect(); };
   }, []);

   const isSmall = windowWidth < 640;
   const isMobile = windowWidth < 768;
   return (
      <section style={{ padding: '48px 0 96px', color: 'var(--sp-ink)' }}>
         <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
            <p ref={ref} className="sp-tabular-nums" style={{
               whiteSpace: 'nowrap',
               fontSize: isSmall ? 48 : (isMobile ? 72 : 96),
               fontWeight: 800, letterSpacing: '-0.02em',
               margin: 0,
            }}>
               {count.toLocaleString('en-US')}<span>+</span>
            </p>
            <p style={{ marginTop: 16, marginBottom: 0, fontSize: 18, color: 'var(--sp-text-soft)', textTransform: 'none', fontWeight: 400 }}>
               {t('salesPageNew.counter.description')}
            </p>
            <SocialGrid />
         </div>
      </section>
   );
}
